import { useQuery } from "@tanstack/react-query";
import { useSession } from "@/lib/auth-client";
import { ACCESS_LEVELS, type AccessLevel } from "@/lib/enums";
import type { BookAccess } from "@/lib/profitability-api";
import { accessKeys } from "@/lib/query";

export interface MyAccess {
  level: AccessLevel | null;
  /** Client Partners and CSMs: their own book, no person-level figures. */
  isAggregate: boolean;
}

/**
 * The signed-in user's access level, for shaping navigation and dropping
 * columns that would only ever be empty. Never the boundary: the engine scopes
 * and redacts every response itself, and the pages also cope with an absent
 * key on their own.
 *
 * Unknown or missing reads as aggregate -- the narrower view -- matching the
 * fail-closed tier on the server.
 */
export async function fetchMyAccess(): Promise<MyAccess> {
  const response = await fetch("/api/me/access");
  const body = response.ok
    ? ((await response.json().catch(() => ({}))) as { level?: string | null })
    : {};
  const level = ACCESS_LEVELS.find((l) => l === body.level) ?? null;
  return { level, isAggregate: level !== "admin" && level !== "full" };
}

export function useMyAccess() {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? "";
  return useQuery({
    queryKey: accessKeys.me(userId),
    queryFn: fetchMyAccess,
    enabled: userId !== "",
    staleTime: 60_000,
  });
}

/**
 * Whether this screen should drop person-level columns (cost below the
 * account, hours, rates, people). True when the response itself says it was
 * scoped to a book -- the engine's own word -- or when the signed-in level is
 * Aggregate, so a column never flashes in before the data arrives.
 */
export function useHidesPersonLevel(access: BookAccess | undefined): boolean {
  const { data } = useMyAccess();
  return access !== undefined || data?.isAggregate === true;
}

/**
 * Whether the P&L statement may be requested: only once the level is KNOWN to
 * be Full or Admin. Stricter than `useHidesPersonLevel`, which shows columns
 * while the level loads -- a pasted ?tab=pnl link would otherwise fire a
 * request the engine refuses before the page knows who is asking.
 */
export function useMayRequestPnl(access: BookAccess | undefined): boolean {
  const { data } = useMyAccess();
  return access === undefined && data?.isAggregate === false;
}
